import { Metadata } from 'lib/rich/metadata'
import { InstanceProperties, RichType } from 'lib/rich/rich-type'
import { UnitShorthand } from 'types/unit-shorthand'
import { unit_metadata } from './common'

/*
  // Old Prisma structure for reference
  spindlePower     Float? @map("spindle_power")
  toolChangeTime   Float? @map("tool_change_time")
  unit             Unit   @default(INCHES)
  travel           Json?  @map("travel")
  maximumFeedRates Json?  @map("maximum_feed_rates")
  spindleSpeeds    Json?  @map("spindle_speeds")
  rapidFeedRate    Float? @map("rapid_feed_rate")
  maxAccelerations Json?  @map("max_accelerations")
*/

const axis_travel: Omit<Metadata, 'title'> = {
  kind: 'length',
  default_value_in: 40.0,
  default_value_mm: 1000.0,
  required: true,
  gt: 0.0,
  inGroup: true,
}

const rapid_feedrate: Omit<Metadata, 'title'> = {
  kind: 'feedrate',
  default_value_in: 800.0,
  default_value_mm: 20000.0,
  required: true,
  gt: 0.0,
  inGroup: true,
}

const acceleration: Omit<Metadata, 'title'> = {
  kind: 'acceleration',
  default_value_in: 200.0,
  default_value_mm: 5000.0,
  required: true,
  gt: 0.0,
  inGroup: true,
}

export class Machine extends RichType {
  static unit_metadata: Metadata = { ...unit_metadata, hidden: false }
  unit: UnitShorthand

  static vendor_metadata: Metadata = {
    title: 'Vendor',
    kind: 'text',
    default_value: '',
  }
  vendor: string

  // Max Travel

  static travel_metadata: Metadata = {
    kind: 'group',
    title: 'Maximum Travel',
    description: 'The range of each axis.',
  }

  static travelX_metadata: Metadata = {
    title: 'X Axis Travel',
    inlineTitle: 'X Axis',
    ...axis_travel,
  }
  travelX: number

  static travelY_metadata: Metadata = {
    title: 'Y Axis Travel',
    inlineTitle: 'Y Axis',
    ...axis_travel,
  }
  travelY: number

  static travelZ_metadata: Metadata = {
    title: 'Z Axis Travel',
    inlineTitle: 'Z Axis',
    ...axis_travel,
    default_value_in: 10.0,
    default_value_mm: 250.0,
  }
  travelZ: number

  // Rapid Feedrates

  static rapidFeedrate_metadata: Metadata = {
    kind: 'group',
    title: 'Rapid Feedrates',
    description: 'The maximum rapid feedrate of each axis.',
  }

  static rapidFeedrateX_metadata: Metadata = {
    title: 'X Rapid Feedrate',
    inlineTitle: 'X Axis',
    ...rapid_feedrate,
  }
  rapidFeedrateX: number

  static rapidFeedrateY_metadata: Metadata = {
    title: 'Y Rapid Feedrate',
    inlineTitle: 'Y Axis',
    ...rapid_feedrate,
  }
  rapidFeedrateY: number

  static rapidFeedrateZ_metadata: Metadata = {
    title: 'Z Rapid Feedrate',
    inlineTitle: 'Z Axis',
    ...rapid_feedrate,
  }
  rapidFeedrateZ: number

  // Accelerations

  static acceleration_metadata: Metadata = {
    kind: 'group',
    title: 'Acceleration',
    description: 'The maximum acceleration of each axis.',
  }

  static accelerationX_metadata: Metadata = {
    title: 'X Acceleration',
    inlineTitle: 'X Axis',
    ...acceleration,
  }
  accelerationX: number

  static accelerationY_metadata: Metadata = {
    title: 'Y Acceleration',
    inlineTitle: 'Y Axis',
    ...acceleration,
  }
  accelerationY: number

  static accelerationZ_metadata: Metadata = {
    title: 'Z Acceleration',
    inlineTitle: 'Z Axis',
    ...acceleration,
  }
  accelerationZ: number

  // Tool Change Time

  static toolChangeTime_metadata: Metadata = {
    title: 'Tool Change Time',
    kind: 'duration',
    default_value: 1.0,
    required: true,
    gte: 0.0,
    inGroup: false,
  }
  toolChangeTime: number

  static version = 1
}

export type MachineData = InstanceProperties<Machine>
